/* eslint-disable max-statements */
import { useKeystoneSession } from '../../../auth/use-keystone-session';
import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Text, useColorMode, useTheme } from '@chakra-ui/react';
import { useContext, useEffect } from 'react';

import { AppContext } from '@context';
import { Logo } from '../../groups/logo-group/logo';
import { bootstrapApp, ContentPanelMode, ContextPanelMode, pushContextPanel, selectContentPanelMode, selectIsBootstrapped, selectIsNavigationReady, selectNavigation, selectUseTestContact, selectWhatsLoading, setContentPanelMode, useAppDispatch, useAppSelector } from '@state';

export const AppLoadingPanel = () => {
	const { status, signIn, error } = useKeystoneSession();
	const isAuthenticated = status === 'ready';
	const isAuthLoading = status === 'loading';

	const { appState: { customerDesigns: { isLoading, isLoadingMessage }}} = useContext(AppContext);

	const dispatch = useAppDispatch();
	const contentPanelMode = useAppSelector(selectContentPanelMode);
	const isBootstrapped = useAppSelector(selectIsBootstrapped);
	const isNavigationReady = useAppSelector(selectIsNavigationReady);
	const whatsLoading = useAppSelector(selectWhatsLoading);
	const navigation = useAppSelector(selectNavigation);
	const useTestContact = useAppSelector(selectUseTestContact);
	const theme = useTheme();
	const { colorMode } = useColorMode();

	// console.log('render:AppLoadingPanel (state:)', isBootstrapped, auth, navigation);

	// Once auth is ready and loaded, and we have a valid route, bootstrap the app
	// Guard: wait for navigation to be parsed from the URL before making routing decisions
	useEffect(() => {
		if (!isNavigationReady || isAuthLoading || !isAuthenticated) return;
		if (navigation.email) {
			// bootstrap the app with customer data
			// Reads the stored choice rather than the env default, so re-bootstrapping (a customer switch)
			// cannot silently revert the designer's Test Contact toggle behind their back.
			dispatch(bootstrapApp({ email: navigation.email, useTestContact }));
			// transition away from find-customer into designer — the content panel
			// decides whether to show the design or the new-submission panel once
			// designs have loaded (avoids the old bug where NewSubmission flashed
			// before designs were fetched)
			if (contentPanelMode === ContentPanelMode.findCustomer) {
				dispatch(setContentPanelMode(ContentPanelMode.Designer));
			}
		} else if (contentPanelMode !== ContentPanelMode.findCustomer) {
			// Only switch to find-customer if not already showing it.
			// The handler in useFindCustomerFeature sets this mode before navigating,
			// so this branch handles the initial load case (no email in URL).
			dispatch(setContentPanelMode(ContentPanelMode.findCustomer));
			dispatch(pushContextPanel({ mode: ContextPanelMode.FindCustomerProperties, temporary: true }));
		}
	}, [isNavigationReady, isAuthLoading, isAuthenticated, navigation.email]);


	const whatsLoadingSection = isAuthLoading
		? <Text fontSize="2xl" fontWeight="bold" textAlign="center" color="black" >Checking Login</Text>
		: <Text fontSize="2xl" fontWeight="bold" textAlign="center" color="black" >{whatsLoading}{whatsLoading ? <br/> : ''}{isLoadingMessage}</Text>;

	const loginSection = <>
		<Button minW={'200px'} fontSize="xl" fontWeight="bold" onClick={() => signIn()}>Login</Button>
		<Text fontSize="2xl" fontWeight="bold" textAlign="center" color="black" >Please Login to Continue</Text>
	</>;

	let progressSection = null;
	if (!isAuthLoading && !isAuthenticated) {
		progressSection = loginSection;
	} else if (!isNavigationReady || !isBootstrapped || isLoading) {
		progressSection = whatsLoadingSection;
	}
	// console.log('AppLoadingPanel (state:->progressSection)', progressSection, isBootstrapped, isAuthLoading, isAuthenticated);

	const panel = !isAuthenticated || !isNavigationReady || !isBootstrapped || isLoading
		? <>
			<Box top={0} left={0} pos={'fixed'} w={'100%'} h={'100%'} zIndex={theme.z.overlay} bg={colorMode === 'dark' ? 'black' : 'white'} opacity={0.80}>
			</Box>
			<Box top={0} left={0} pos={'fixed'} w={'100%'} h={'100%'} zIndex={theme.z.overlay} display="flex" textAlign={'center'} justifyContent="center" alignItems="center">
				<Box minW="300px" w="50%" position={'fixed'} >
					<Logo pb="30px"/>
					{progressSection}
					{ error && <Box pt="20px">
						<Alert status="error">
							<AlertIcon />
							<AlertTitle>Login Error:</AlertTitle>
							<AlertDescription>{error.message}</AlertDescription>
						</Alert>
					</Box> }
				</Box>
			</Box>
		</>
		: null;

	// console.log('render:AppLoadingPanel');
	return (
		<>
			{panel}
		</>
	);
};
